// Sign-in against the API's own front door (design/auth-v4.html): one POST
// to sign in, one to refresh, one DELETE to sign out. The session (access
// token, refresh token, expiry) lives in localStorage. No provider, no SDK,
// no key in the bundle.

const KEY = 'rp-auth';

export type Session = {
  accessToken: string;
  /** epoch ms when the access token expires */
  accessExpiresAt: number;
  refreshToken: string;
  email: string | null;
  userId: string;
};

export function session(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function save(s: Session): Session {
  localStorage.setItem(KEY, JSON.stringify(s));
  return s;
}

export class AuthError extends Error {}

type SessionView = { accessToken: string; accessExpiresAt: string; refreshToken: string; user: { userId: string; email: string | null } };

async function front(path: string, body: unknown, bearer?: string): Promise<Response> {
  return fetch(`/api/v1${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}) },
    body: JSON.stringify(body),
  });
}

function fromView(v: SessionView): Session {
  return { accessToken: v.accessToken, accessExpiresAt: new Date(v.accessExpiresAt).getTime(), refreshToken: v.refreshToken, email: v.user.email, userId: v.user.userId };
}

async function errorOf(resp: Response, fallback: string): Promise<AuthError> {
  try {
    const e = ((await resp.json()) as { error?: { code?: string; message?: string } }).error;
    return new AuthError(e?.message ?? fallback);
  } catch {
    return new AuthError(fallback);
  }
}

export async function signIn(email: string, password: string): Promise<Session> {
  const resp = await front('/auth/sessions', { email, password });
  if (!resp.ok) throw await errorOf(resp, 'Sign-in failed.');
  return save(fromView((await resp.json()) as SessionView));
}

/** A password link (invite or reset) sets the password and signs in. */
export async function redeemPasswordLink(token: string, password: string): Promise<Session> {
  const resp = await front(`/auth/password-links/${encodeURIComponent(token)}/redeem`, { password });
  if (!resp.ok) throw await errorOf(resp, 'This link could not be used.');
  return save(fromView((await resp.json()) as SessionView));
}

export async function signOut(): Promise<void> {
  const s = session();
  localStorage.removeItem(KEY);
  if (s) {
    try {
      await fetch('/api/v1/auth/sessions/current', { method: 'DELETE', headers: { Authorization: `Bearer ${s.accessToken}` } });
    } catch {
      /* the token dies with its expiry regardless */
    }
  }
}

/** A valid access token, refreshing behind the scenes when within a minute of expiry. */
export async function freshToken(): Promise<string> {
  const s = session();
  if (!s) throw new AuthError('signed out');
  if (Date.now() < s.accessExpiresAt - 60_000) return s.accessToken;
  const resp = await front('/auth/sessions/refresh', { refreshToken: s.refreshToken });
  if (!resp.ok) {
    if (resp.status === 401) {
      localStorage.removeItem(KEY);
      throw new AuthError('session expired');
    }
    if (Date.now() < s.accessExpiresAt) return s.accessToken; // transient failure; still usable
    throw new AuthError(`token refresh failed (${resp.status})`);
  }
  return save(fromView((await resp.json()) as SessionView)).accessToken;
}
